// Pure helpers for the 12×8 dashboard wall layout. The wall page owns
// React state; this module only knows about geometry. All callers
// pass and receive plain `DashboardTile[]` arrays — no mutation.
//
// Coordinate system: 1-indexed CSS-grid lines, matching the wire model.
// `col_start ∈ [1..12]`, `row_start ∈ [1..8]`, spans must keep the tile
// inside the 12×8 box (col_start + col_span - 1 ≤ 12, etc.).

import type { DashboardTile } from "@/types/dashboard";
import {
  DASHBOARD_GRID_COLS,
  DASHBOARD_GRID_ROWS,
  DASHBOARD_MAX_TILES,
} from "@/types/dashboard";

/** Default tile footprint used by both auto-place and reset-layout. */
export const DEFAULT_TILE_COL_SPAN = 3;
export const DEFAULT_TILE_ROW_SPAN = 2;

/** Inclusive 1-indexed rectangle (matches CSS grid lines). */
interface Rect {
  col_start: number;
  row_start: number;
  col_span: number;
  row_span: number;
}

/** True iff the two rectangles share any grid cell. */
export function tilesOverlap(a: Rect, b: Rect): boolean {
  const aColEnd = a.col_start + a.col_span - 1;
  const bColEnd = b.col_start + b.col_span - 1;
  const aRowEnd = a.row_start + a.row_span - 1;
  const bRowEnd = b.row_start + b.row_span - 1;
  if (aColEnd < b.col_start || bColEnd < a.col_start) return false;
  if (aRowEnd < b.row_start || bRowEnd < a.row_start) return false;
  return true;
}

/** True iff the rectangle fits inside the 12×8 box. */
function fitsInGrid(r: Rect): boolean {
  if (r.col_start < 1 || r.row_start < 1) return false;
  if (r.col_span < 1 || r.row_span < 1) return false;
  if (r.col_start + r.col_span - 1 > DASHBOARD_GRID_COLS) return false;
  if (r.row_start + r.row_span - 1 > DASHBOARD_GRID_ROWS) return false;
  return true;
}

/**
 * Scan the 12×8 grid in row-major order and return the first cell
 * where a `colSpan × rowSpan` block fits without overlapping any
 * tile in `tiles`. Returns null if no such cell exists.
 */
export function findEmptySlot(
  tiles: DashboardTile[],
  colSpan: number = DEFAULT_TILE_COL_SPAN,
  rowSpan: number = DEFAULT_TILE_ROW_SPAN,
): { col_start: number; row_start: number } | null {
  for (let row = 1; row <= DASHBOARD_GRID_ROWS - rowSpan + 1; row++) {
    for (let col = 1; col <= DASHBOARD_GRID_COLS - colSpan + 1; col++) {
      const candidate: Rect = {
        col_start: col,
        row_start: row,
        col_span: colSpan,
        row_span: rowSpan,
      };
      const collides = tiles.some((t) => tilesOverlap(t, candidate));
      if (!collides) return { col_start: col, row_start: row };
    }
  }
  return null;
}

/**
 * Place `movingTile` at `newPos` and shift any neighbors out of the
 * way using a fluid, cascading algorithm:
 *
 * 1. The moving tile lands at `newPos`.
 * 2. Each neighbor that overlaps the new footprint is relocated to the
 *    nearest empty slot of its own size, found by a BFS in
 *    Manhattan-distance order over the 12×8 grid. Neighbors that already
 *    overlap with the cascading-relocation set are themselves cascaded
 *    out of the way.
 * 3. If any neighbor cannot find a slot at its current size we shrink it
 *    by one row/col (whichever is larger) and try again, down to 1×1.
 *
 * This mirrors the "fluid auto-adjust" UX in the mockup — drop a tile
 * anywhere and the rest of the wall reflows around it. Throws `no-room`
 * only when even the 1×1 fallback can't be placed (i.e. the wall is
 * already at 96 cells / 16 max tiles and full).
 *
 * The returned array has the same length as `tiles`; tile order is
 * preserved.
 */
export function resolveCollision(
  tiles: DashboardTile[],
  movingTile: DashboardTile,
  newPos: Rect,
): DashboardTile[] {
  const clamped = clampToGrid(newPos);
  if (!fitsInGrid(clamped)) {
    throw new Error("no-room");
  }
  const moved: DashboardTile = {
    ...movingTile,
    col_start: clamped.col_start,
    row_start: clamped.row_start,
    col_span: clamped.col_span,
    row_span: clamped.row_span,
  };

  // Working set: id → tile. Apply the move first.
  const placed = new Map<string, DashboardTile>();
  placed.set(moved.tile_id, moved);
  // Pending: tiles still needing a home, in original (committed) shape.
  const pending: DashboardTile[] = tiles
    .filter((t) => t.tile_id !== moved.tile_id)
    .map((t) => ({ ...t }));

  // BFS over the grid in Manhattan distance order from the tile's
  // committed origin so the displacement looks "nudgey" rather than
  // teleporting across the wall.
  const findNearestSlot = (
    spanC: number,
    spanR: number,
    fromCol: number,
    fromRow: number,
    occupied: Map<string, DashboardTile>,
    excludeId: string | null,
  ): { col_start: number; row_start: number } | null => {
    const candidates: { col: number; row: number; d: number }[] = [];
    for (let row = 1; row <= DASHBOARD_GRID_ROWS - spanR + 1; row++) {
      for (let col = 1; col <= DASHBOARD_GRID_COLS - spanC + 1; col++) {
        const d = Math.abs(col - fromCol) + Math.abs(row - fromRow);
        candidates.push({ col, row, d });
      }
    }
    candidates.sort((a, b) => a.d - b.d);
    for (const c of candidates) {
      const candidate: Rect = {
        col_start: c.col,
        row_start: c.row,
        col_span: spanC,
        row_span: spanR,
      };
      let collides = false;
      for (const [id, t] of occupied) {
        if (id === excludeId) continue;
        if (tilesOverlap(t, candidate)) {
          collides = true;
          break;
        }
      }
      if (!collides) return { col_start: c.col, row_start: c.row };
    }
    return null;
  };

  // Process pending tiles in order of overlap-with-moved first, then the
  // rest (so the displaced ones are re-placed before potentially
  // displacing them again).
  pending.sort((a, b) => {
    const aOverlap = tilesOverlap(a, moved) ? 0 : 1;
    const bOverlap = tilesOverlap(b, moved) ? 0 : 1;
    return aOverlap - bOverlap;
  });

  for (const t of pending) {
    // First check if the tile fits in its committed position without
    // colliding with anything already placed.
    let conflict = false;
    for (const [id, p] of placed) {
      if (id === t.tile_id) continue;
      if (tilesOverlap(p, t)) {
        conflict = true;
        break;
      }
    }
    if (!conflict) {
      placed.set(t.tile_id, t);
      continue;
    }
    // Find the nearest empty slot for the original span; shrink as a
    // fallback if the wall is too crowded.
    let spanC = t.col_span;
    let spanR = t.row_span;
    let slot: { col_start: number; row_start: number } | null = null;
    while (slot === null && spanC >= 1 && spanR >= 1) {
      slot = findNearestSlot(spanC, spanR, t.col_start, t.row_start, placed, null);
      if (slot) break;
      // Shrink the larger axis first.
      if (spanC >= spanR && spanC > 1) spanC--;
      else if (spanR > 1) spanR--;
      else break;
    }
    if (!slot) {
      throw new Error("no-room");
    }
    placed.set(t.tile_id, {
      ...t,
      col_start: slot.col_start,
      row_start: slot.row_start,
      col_span: spanC,
      row_span: spanR,
    });
  }

  // Emit in original order so React keys stay stable.
  return tiles.map((t) => placed.get(t.tile_id) ?? t);
}

/**
 * Reset `tiles` to a default 4×4 grid: each tile takes a 3×2 cell,
 * placed in row-major order. tile_id and doorbot_id are preserved;
 * spans are reset to 3×2 even if the caller had grown one.
 */
export function resetLayout(tiles: DashboardTile[]): DashboardTile[] {
  const out: DashboardTile[] = [];
  const limit = Math.min(tiles.length, DASHBOARD_MAX_TILES);
  for (let i = 0; i < limit; i++) {
    const row = Math.floor(i / 4); // 4 tiles per row
    const col = i % 4;
    out.push({
      ...tiles[i]!,
      col_start: col * DEFAULT_TILE_COL_SPAN + 1,
      row_start: row * DEFAULT_TILE_ROW_SPAN + 1,
      col_span: DEFAULT_TILE_COL_SPAN,
      row_span: DEFAULT_TILE_ROW_SPAN,
    });
  }
  return out;
}

/**
 * Clamp a candidate rectangle inside the 12×8 grid (grow/shrink the
 * span as needed). Used during resize to stop a drag from running off
 * the wall.
 */
export function clampToGrid(r: Rect): Rect {
  let { col_start, row_start, col_span, row_span } = r;
  if (col_start < 1) col_start = 1;
  if (row_start < 1) row_start = 1;
  if (col_span < 1) col_span = 1;
  if (row_span < 1) row_span = 1;
  if (col_start + col_span - 1 > DASHBOARD_GRID_COLS) {
    col_span = DASHBOARD_GRID_COLS - col_start + 1;
  }
  if (row_start + row_span - 1 > DASHBOARD_GRID_ROWS) {
    row_span = DASHBOARD_GRID_ROWS - row_start + 1;
  }
  return { col_start, row_start, col_span, row_span };
}

/**
 * Swap the `(col_start, row_start)` of two tiles. Used by drag-to-
 * reorder in the wall page. Spans are preserved; if the swap would
 * push either tile off-grid (because the spans differ) the swap is
 * rejected and the original array is returned unchanged.
 */
export function swapPositions(
  tiles: DashboardTile[],
  aId: string,
  bId: string,
): DashboardTile[] {
  const a = tiles.find((t) => t.tile_id === aId);
  const b = tiles.find((t) => t.tile_id === bId);
  if (!a || !b) return tiles;
  const aAtB: Rect = {
    col_start: b.col_start,
    row_start: b.row_start,
    col_span: a.col_span,
    row_span: a.row_span,
  };
  const bAtA: Rect = {
    col_start: a.col_start,
    row_start: a.row_start,
    col_span: b.col_span,
    row_span: b.row_span,
  };
  if (!fitsInGrid(aAtB) || !fitsInGrid(bAtA)) return tiles;
  return tiles.map((t) => {
    if (t.tile_id === a.tile_id) {
      return { ...t, col_start: aAtB.col_start, row_start: aAtB.row_start };
    }
    if (t.tile_id === b.tile_id) {
      return { ...t, col_start: bAtA.col_start, row_start: bAtA.row_start };
    }
    return t;
  });
}

/**
 * True iff two tile arrays describe the same layout (same set of
 * tile_ids with the same doorbot_id + grid coordinates). Order-
 * insensitive. Used to drive the "Unsaved changes" indicator.
 */
export function tilesLayoutEqual(
  a: DashboardTile[],
  b: DashboardTile[],
): boolean {
  if (a.length !== b.length) return false;
  const byId = new Map(a.map((t) => [t.tile_id, t]));
  for (const t of b) {
    const m = byId.get(t.tile_id);
    if (!m) return false;
    if (
      m.doorbot_id !== t.doorbot_id ||
      m.col_start !== t.col_start ||
      m.row_start !== t.row_start ||
      m.col_span !== t.col_span ||
      m.row_span !== t.row_span
    ) {
      return false;
    }
  }
  return true;
}
